import React, { useState, useEffect } from "react";
import * as d3 from "d3";
import { Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const twoColors = ["#64BDE1", "#BEBEBE"];
const threeColors = ["#64BDE1", "#7DC199", "#C275A1"];
const variousColors = [
  "#64BDE1",
  "#BEBEBE",
  "#FBD560",
  "#C275A1",
  "#5BB27F",
  "#E5AB5F",
];

export default function BarPlot({
  dimensions = { width: undefined, height: undefined, margin: undefined },
  data,
  valueOfInterest,
  text,
  className,
  subtext,

  centerLabel,
  valueToDisplay,
  keepDataOrder,
  displayValueAsPercent,
  colorSchema,
  noTotal,
  fontSize = 16,
  isPercentage,
  hidePercent,
}: {
  dimensions?: {
    width?: number;
    height?: number;
    margin?: number;
  };
  data: { [key: string]: number };
  valueOfInterest?: string;
  text?: string[];
  className?: string;
  subtext?: string[];
  centerLabel?: string;
  valueToDisplay?: number;
  fontSize?: number;
  keepDataOrder?: boolean;
  displayValueAsPercent?: boolean;
  colorSchema?: string[];
  noTotal?: boolean;
  isPercentage?: boolean;
  hidePercent?: boolean;
}) {
  const svgContainerRef = React.useRef<HTMLDivElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);

  const [dataKey, setDataKey] = useState(Date.now());
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [svgHeight, setSvgHeight] = useState(0);
  const [svgWidth, setSvgWidth] = useState(0);

  useEffect(() => {
    const updateSize = () => {
      if (!svgContainerRef.current) return;
      const { clientHeight, clientWidth } = svgContainerRef.current;
      setSvgHeight(dimensions.height ?? clientHeight);
      setSvgWidth(dimensions.width ?? clientWidth);
    };

    updateSize();

    if (dimensions.width && dimensions.height) return;

    const observer = new ResizeObserver(() => updateSize());
    if (svgContainerRef.current) {
      observer.observe(svgContainerRef.current);
    }

    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [dimensions.height, dimensions.width]);

  const { margin = fontSize } = dimensions;

  useEffect(() => {
    setDataKey(Date.now());
  }, [data, dimensions, valueOfInterest, keepDataOrder, colorSchema]);

  const total = Object.values(data).reduce((acc, d) => acc + d, 0);

  // Transform data for bar chart
  const transformedData = Object.keys(data).map((key) => ({
    key,
    value: data[key],
  }));

  // Sort data if needed
  if (!keepDataOrder) {
    transformedData.sort((a, b) => {
      if (a.key === valueOfInterest) {
        return -1;
      }
      if (b.key === valueOfInterest) {
        return 1;
      }
      return b.value - a.value;
    });
  }

  const domain = transformedData.map((d) => d.key);
  const plotColors = colorSchema
    ? colorSchema
    : domain.length <= 2
      ? twoColors
      : domain.length <= 3
        ? threeColors
        : variousColors;

  // set the color scale
  const color = d3.scaleOrdinal().domain(domain).range(plotColors);

  // Setup scales for bar chart
  const xScale = d3
    .scaleBand()
    .domain(domain)
    .range([0, svgWidth])
    .padding(0.1);

  // Determine bar width based on number of categories
  const useFixedWidth = domain.length <= 4;
  const fixedBarWidth = 90; // Fixed width for small number of bars
  const barPadding = domain.length > 2 ? 15 : 50; // Padding between fixed-width bars

  // Adjust xScale for fixed width bars if needed
  if (useFixedWidth) {
    // Center the bars when using fixed width
    const totalBarsWidth = domain.length * fixedBarWidth;
    const totalPaddingWidth = (domain.length - 1) * barPadding;
    const startX = (svgWidth - (totalBarsWidth + totalPaddingWidth)) / 2;

    // Create custom positioning function for fixed width bars
    xScale.range([startX, startX + totalBarsWidth + totalPaddingWidth]);
  }

  const yMax = d3.max(transformedData, (d) => d.value) || 0;

  const yScale = d3
    .scaleLinear()
    .domain([0, yMax])
    .range([svgHeight - margin * 2, margin + 25]);

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const handleMouseOver = (index: number, event: React.MouseEvent) => {
    setHoveredBar(index);
  };
  const handleMouseOut = () => {
    setHoveredBar(null);
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const handleMouseMove = (_event: React.MouseEvent) => {
    // We'll use fixed positioning based on the bar position
    // instead of tracking mouse movements
  };

  return (
    <>
      <div
        className="relative flex flex-col items-start p-4 h-full"
        data-testid="categorical-plot-container"
        data-plot-title={text?.[0]}
      >
        {/* Title and subtitle moved outside SVG */}
        {text && text[0] && (
          <div className="text-[14px] font-work-sans text-center flex items-center gap-1">
            {text[0]}
            {centerLabel && (
              <Tooltip title={centerLabel}>
                <QuestionCircleOutlined />
              </Tooltip>
            )}
          </div>
        )}

        {text && text[1] && (
          <div className="text-2xl font-work-sans text-center">{text[1]}</div>
        )}

        {valueOfInterest || valueToDisplay ? (
          <div className="text-4xl font-semibold font-work-sans text-center">
            {valueToDisplay ||
              (valueOfInterest ? data[valueOfInterest] : undefined)}
            {displayValueAsPercent && "%"}
            {!displayValueAsPercent && !noTotal && total && `/${total}`}
          </div>
        ) : (
          subtext?.map((text, i) => (
            <div
              key={i}
              className="text-[12px] font-work-sans text-left text-[#8C8C8C]"
            >
              {text}
            </div>
          ))
        )}

        <div className="w-full flex-1 min-h-0" ref={svgContainerRef}>
          <svg
            key={dataKey}
            ref={svgRef}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            preserveAspectRatio="xMidYMid meet"
            className={`${className} plot`}
            id={className}
          >
            {/* Tooltip */}

            {/* X-axis labels */}
            <g>
              {transformedData.map((d, i) => {
                const barWidth = useFixedWidth
                  ? fixedBarWidth
                  : xScale.bandwidth();
                const barX = xScale(d.key) ?? 0;
                const x = useFixedWidth
                  ? barX + (xScale.bandwidth() - barWidth) / 2
                  : barX;
                const centerX = x + barWidth / 2;

                return (
                  <text
                    key={`label-${i}`}
                    x={centerX}
                    y={svgHeight - fontSize / 2}
                    textAnchor="middle"
                    fontSize={fontSize || "16"}
                    fontFamily="Work Sans"
                  >
                    {d.key}
                  </text>
                );
              })}
            </g>

            {/*Tooltip*/}

            <g>
              {transformedData.map((d, i) => {
                const barX = xScale(d.key) ?? 0;
                const y = yScale(d.value);
                // Use fixed width for small number of bars, otherwise use bandwidth
                const barWidth = useFixedWidth
                  ? fixedBarWidth
                  : xScale.bandwidth();
                // Center the bar within its allocated space if using fixed width
                const x = useFixedWidth
                  ? barX + (xScale.bandwidth() - barWidth) / 2
                  : barX;
                const height =
                  svgHeight - yScale(d.value) - margin * 2 || fontSize * 2;
                const radius = 5; // Corner radius

                // Path for rectangle with rounded top corners only
                const path = `
              M ${x},${y + radius}
              Q ${x},${y} ${x + radius},${y}
              L ${x + barWidth - radius},${y}
              Q ${x + barWidth},${y} ${x + barWidth},${y + radius}
              L ${x + barWidth},${y + height}
              L ${x},${y + height}
              Z
            `;

                return (
                  <g key={i}>
                    {d.value > 0 && (
                      <path
                        d={path}
                        fill={color(d.key) as string}
                        onMouseOver={(e) => handleMouseOver(i, e)}
                        onMouseOut={handleMouseOut}
                        onMouseMove={handleMouseMove}
                        className={`bar-${i}-${d.key}`}
                      />
                    )}
                    {/* {hoveredBar === i && ( */}
                    <g>
                      {/* <rect
                        x={x}
                        y={y - 30}
                        width={barWidth}
                        height={30}
                        fill={"white"}
                        stroke={"black"}
                        strokeWidth={1}
                      /> */}
                      <text
                        x={x + barWidth / 2}
                        y={y - 10}
                        fontSize={15}
                        fontFamily="Work Sans"
                        fill={"black"}
                        textAnchor="middle"
                        data-testid="categorical-plot-value"
                        data-plot-key={d.key}
                      >
                        {d.value}
                        {isPercentage
                          ? "%"
                          : !hidePercent &&
                            ` (${Math.round((d.value / total) * 100)}%)`}
                      </text>
                    </g>
                    {/* )} */}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
        {/* Tooltip */}
      </div>
    </>
  );
}
